/**
 * boardSetup.ts — Programmatic board provisioning utility.
 *
 * Called by the setup script (scripts/setupBoards.ts) and also available
 * as an in-app "First Time Setup" wizard to create the five Chanelle OS
 * boards when a new user installs the app.
 *
 * Creates:
 *   1. Topics board
 *   2. Notes board
 *   3. Actions board
 *   4. Meetings board
 *   5. Settings board
 *
 * After creation, stores all board IDs in the monday Storage API so the
 * app can reference them without requiring the user to configure anything.
 */

import {
  mutateCreateBoard,
  mutateCreateColumn,
  mutateCreateGroup,
  mutateDeleteBoard,
} from '../api/mutations';
import { storageSetJSON, storageDelete } from '../api/client';
import { STORAGE_KEYS, CHANELLE_OS_BOARD_NAMES } from './defaults';
import type { ChanelleOSBoardIds, BoardIdMap } from '../types';

// -------------------------------------------------------
// Column creation helpers
// -------------------------------------------------------

type ColDef = {
  title: string;
  type: string;
  settings?: Record<string, unknown>;
};

async function addColumns(boardId: string, cols: ColDef[]): Promise<void> {
  for (const col of cols) {
    await mutateCreateColumn(boardId, col.title, col.type, col.settings);
  }
}

// -------------------------------------------------------
// Board provisioners
// -------------------------------------------------------

async function createTopicsBoard(): Promise<string> {
  const board = await mutateCreateBoard(CHANELLE_OS_BOARD_NAMES.topics, 'share');
  const boardId = board.id;

  await addColumns(boardId, [
    { title: 'Category',      type: 'dropdown' },
    { title: 'Status',        type: 'status' },
    { title: 'Owner',         type: 'people' },
    { title: 'Last Updated',  type: 'date' },
    { title: 'Priority',      type: 'status' },
    { title: 'Notes Count',   type: 'numbers' },
    { title: 'Actions Count', type: 'numbers' },
    { title: 'Description',   type: 'long_text' },
    { title: 'Archived Date', type: 'date' },
  ]);

  await mutateCreateGroup(boardId, 'Active Topics');
  await mutateCreateGroup(boardId, 'Archived Topics');

  return boardId;
}

async function createNotesBoard(): Promise<string> {
  const board = await mutateCreateBoard(CHANELLE_OS_BOARD_NAMES.notes, 'share');
  const boardId = board.id;

  await addColumns(boardId, [
    { title: 'Type',         type: 'dropdown' },
    { title: 'Content',      type: 'long_text' },
    { title: 'Created By',   type: 'people' },
    { title: 'Created Date', type: 'date' },
    { title: 'Last Updated', type: 'date' },
    { title: 'Pinned',       type: 'checkbox' },
    { title: 'Status',       type: 'status' },
    { title: 'Source URL',   type: 'link' },
    { title: 'Word Count',   type: 'numbers' },
  ]);

  await mutateCreateGroup(boardId, 'Pinned');
  await mutateCreateGroup(boardId, 'All Notes');
  await mutateCreateGroup(boardId, 'Archived');

  return boardId;
}

async function createActionsBoard(): Promise<string> {
  const board = await mutateCreateBoard(CHANELLE_OS_BOARD_NAMES.actions, 'share');
  const boardId = board.id;

  await addColumns(boardId, [
    { title: 'Owner',              type: 'people' },
    { title: 'Status',             type: 'status' },
    { title: 'Priority',           type: 'dropdown' },
    { title: 'Due Date',           type: 'date' },
    { title: 'Escalated',          type: 'checkbox' },
    { title: 'Escalated To Board', type: 'text' },
    { title: 'Escalated Item ID',  type: 'numbers' },
    { title: 'Escalated Item URL', type: 'link' },
    { title: 'Context',            type: 'long_text' },
    { title: 'Estimated Hours',    type: 'numbers' },
    { title: 'Completed Date',     type: 'date' },
  ]);

  await mutateCreateGroup(boardId, 'To Do');
  await mutateCreateGroup(boardId, 'In Progress');
  await mutateCreateGroup(boardId, 'Done');
  await mutateCreateGroup(boardId, 'Blocked');

  return boardId;
}

async function createMeetingsBoard(): Promise<string> {
  const board = await mutateCreateBoard(CHANELLE_OS_BOARD_NAMES.meetings, 'share');
  const boardId = board.id;

  await addColumns(boardId, [
    { title: 'Date',             type: 'date' },
    { title: 'Attendees',        type: 'people' },
    { title: 'Meeting Type',     type: 'dropdown' },
    { title: 'Status',           type: 'status' },
    { title: 'Recording Link',   type: 'link' },
    { title: 'Agenda',           type: 'long_text' },
    { title: 'Summary',          type: 'long_text' },
    { title: 'Duration (mins)',  type: 'numbers' },
    { title: 'Follow Up Date',   type: 'date' },
  ]);

  await mutateCreateGroup(boardId, 'Upcoming');
  await mutateCreateGroup(boardId, 'Completed');
  await mutateCreateGroup(boardId, 'Cancelled');

  return boardId;
}

async function createSettingsBoard(): Promise<string> {
  const board = await mutateCreateBoard(CHANELLE_OS_BOARD_NAMES.settings, 'private');
  const boardId = board.id;

  await addColumns(boardId, [
    { title: 'User ID',             type: 'text' },
    { title: 'Theme',               type: 'dropdown' },
    { title: 'Default Tab',         type: 'dropdown' },
    { title: 'Active Preset',       type: 'dropdown' },
    { title: 'Widget Layout JSON',  type: 'long_text' },
    { title: 'Notification Prefs',  type: 'long_text' },
    { title: 'Watched Board IDs',   type: 'long_text' },
    { title: 'Automation Rules',    type: 'long_text' },
    { title: 'Last Snapshot Date',  type: 'date' },
    { title: 'Snapshot Data',       type: 'long_text' },
    { title: 'Priority Weights',    type: 'long_text' },
  ]);

  await mutateCreateGroup(boardId, 'User Settings');

  return boardId;
}

// -------------------------------------------------------
// Main setup function
// -------------------------------------------------------

export interface SetupResult {
  boardIds: ChanelleOSBoardIds;
  errors: string[];
}

export async function setupChanelleOSBoards(userId: string): Promise<SetupResult> {
  const errors: string[] = [];

  const safeCreate = async (
    name: string,
    fn: () => Promise<string>
  ): Promise<string | null> => {
    try {
      return await fn();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errors.push(`Failed to create ${name} board: ${msg}`);
      return null;
    }
  };

  const [topics, notes, actions, meetings, settings] = await Promise.all([
    safeCreate('Topics',   createTopicsBoard),
    safeCreate('Notes',    createNotesBoard),
    safeCreate('Actions',  createActionsBoard),
    safeCreate('Meetings', createMeetingsBoard),
    safeCreate('Settings', createSettingsBoard),
  ]);

  const boardIds: ChanelleOSBoardIds = {
    topics:   topics   ?? '',
    notes:    notes    ?? '',
    actions:  actions  ?? '',
    meetings: meetings ?? '',
    settings: settings ?? '',
  };

  // Persist board IDs to monday Storage so the app can load them on next launch.
  // Storage only works inside the monday.com app iframe; silently skip in script mode.
  if (errors.length === 0) {
    try {
      const key = STORAGE_KEYS.boardIds(userId);
      await storageSetJSON(key, boardIds);
    } catch {
      // No-op: storage unavailable outside browser context
    }
  }

  return { boardIds, errors };
}

// -------------------------------------------------------
// Load board IDs from Storage (called on app init)
// -------------------------------------------------------

export async function loadBoardIds(userId: string): Promise<ChanelleOSBoardIds | null> {
  const { storageGetJSON } = await import('../api/client');
  const key = STORAGE_KEYS.boardIds(userId);
  return storageGetJSON<ChanelleOSBoardIds>(key);
}

// -------------------------------------------------------
// Auto-discover boards by name (fallback when Storage is empty)
// -------------------------------------------------------

/**
 * Scan the workspace for Chanelle OS boards by matching their names
 * against CHANELLE_OS_BOARD_NAMES (case-insensitive).
 *
 * Returns a populated BoardIdMap if at least the 3 core boards
 * (topics, notes, actions) are found, otherwise null.
 */
export async function discoverBoardsByName(): Promise<BoardIdMap | null> {
  const { queryAllBoards } = await import('../api/queries');

  const boards = await queryAllBoards(200);

  // Build reverse lookup: lowercase board name → board ID
  const nameToId: Record<string, string> = {};
  for (const board of boards) {
    if (board.state === 'deleted') continue;
    nameToId[board.name.trim().toLowerCase()] = board.id;
  }

  // Match against canonical names
  const result: BoardIdMap = {};
  for (const [key, canonicalName] of Object.entries(CHANELLE_OS_BOARD_NAMES)) {
    const id = nameToId[canonicalName.toLowerCase()];
    if (id) {
      (result as Record<string, string>)[key] = id;
    }
  }

  // Require at least the 3 core boards
  if (!result.topics || !result.notes || !result.actions) {
    return null;
  }

  return result;
}

// -------------------------------------------------------
// Reset: delete existing boards then reinstall
// -------------------------------------------------------

/**
 * Wipes the five Chanelle OS boards and recreates them from scratch.
 *
 * Steps:
 *   1. Load existing board IDs from Storage
 *   2. Delete each board (best-effort — failures are swallowed so a partial
 *      previous install doesn't block the reset)
 *   3. Clear the Storage key so the app starts fresh
 *   4. Re-run setupChanelleOSBoards to provision new boards and persist their IDs
 *
 * @param userId  — the monday.com user ID used to namespace Storage keys
 */
export async function resetChanelleOSBoards(userId: string): Promise<SetupResult> {
  // 1. Load any previously stored board IDs
  const existing = await loadBoardIds(userId).catch(() => null);

  // 2. Delete each board that was previously created (best-effort)
  if (existing) {
    const deletions = Object.values(existing)
      .filter(Boolean)
      .map((id) => mutateDeleteBoard(id).catch(() => null));
    await Promise.all(deletions);
  }

  // 3. Remove the Storage key so a stale ID map cannot interfere
  const storageKey = STORAGE_KEYS.boardIds(userId);
  await storageDelete(storageKey).catch(() => null);

  // 4. Provision fresh boards
  return setupChanelleOSBoards(userId);
}
